// Nhập liệu trực tiếp trên AntGrid (prop inlineEdit): các hàm thuần, không phụ thuộc React.
// addForm của lưới là nguồn đúng cho "field nào sửa được, dùng editor gì, bắt buộc gì".

import calculator from './calculator';

export const INLINE_DRAFT_ID = '__ant_inline_draft__';

// `editable` do CẤU HÌNH cột khai báo (không phải giá trị đã tính theo quyền phiếu).
// AntVoucherTabs ghi đè col.editable mỗi lần render nên chụp giá trị gốc vào _configEditable.
export const getConfiguredEditable = (column) => {
    if (!column || typeof column !== 'object') return undefined;
    if (Object.prototype.hasOwnProperty.call(column, '_configEditable'))
        return column._configEditable === null ? undefined : column._configEditable;
    // Có rawConfig (cấu hình gốc) thì chỉ tin nó — col.editable lúc này là giá trị đã TÍNH (có thể false).
    if (column.rawConfig && typeof column.rawConfig === 'object') return column.rawConfig.editable;
    return column.editable;
};

export const isInlineDraftRow = (row) => Boolean(row) && row.id === INLINE_DRAFT_ID;

// Field nhập nhiều dòng: addForm/config/fields dùng editorType textArea (vd description).
export const isMultilineFormItem = (item, fieldConfig) =>
    Boolean(item) && (item.editorType === 'textArea' || item.multiline === true || Boolean(fieldConfig && fieldConfig.editorType === 'textArea'));

// Lá (có dataField) của cây items addForm, bỏ group/tab.
export const flattenFormItems = (items, out = []) => {
    (items instanceof Array ? items : []).forEach((item) => {
        if (!item || typeof item !== 'object') return;
        if (item.items instanceof Array) flattenFormItems(item.items, out);
        if (item.tabs instanceof Array) item.tabs.forEach((tab) => flattenFormItems(tab && tab.items, out));
        if (item.dataField) out.push(item);
    });
    return out;
};

const columnFields = (column) => {
    const fields = [column.dataField];
    (column.fields instanceof Array ? column.fields : []).forEach((f) => fields.push(f && typeof f === 'object' ? f.dataField : f));
    return fields.filter(Boolean);
};

const displayFieldOf = (item) => {
    const name = String(item.dataField || '');
    return name.endsWith('_code') ? name.replace(/_code$/, '_name') : null;
};

const mappedTargets = (item) =>
    (item.options && item.options.mapping instanceof Array ? item.options.mapping : []).map((m) => m && m.to).filter(Boolean);

// Field form KHÔNG nhập được (cố định): chỉ xem, khoá (disabled / disable / readOnly: true) hoặc ẩn (visible: false).
// Điều kiện theo dữ liệu dòng (disable / visible dạng { when }) xét lúc bắt đầu sửa ô — isInlineItemLockedForRow.
export const isInlineItemLocked = (item) =>
    !item ||
    Boolean(item.viewOnly) ||
    item.disabled === true ||
    item.disable === true ||
    item.readOnly === true ||
    item.visible === false;

// Gắn mỗi field addForm (nhập được) với cột lưới đang HIỂN THỊ nó:
//   1) trùng dataField cột; 2) nằm trong fields của cột first/join;
//   3) field *_code của ô chọn danh mục hiển thị ra *_name; 4) đích mapping của ô chọn.
// Một field chỉ gắn một cột; ưu tiên khớp trực tiếp. Trả Map<column.dataField, formItem>.
export const mapInlineFormItems = (columns, formItems) => {
    const result = new Map();
    const items = flattenFormItems(formItems).filter((item) => !isInlineItemLocked(item));
    const cols = (columns instanceof Array ? columns : []).filter((c) => c && typeof c === 'object' && c.dataField && !c.isSystemCol);
    const used = new Set();
    const assign = (predicate) => {
        cols.forEach((column) => {
            if (result.has(column.dataField)) return;
            const item = items.find((i) => !used.has(i) && predicate(column, i));
            if (item) {
                result.set(column.dataField, item);
                used.add(item);
            }
        });
    };
    assign((column, item) => column.dataField === item.dataField);
    assign((column, item) => columnFields(column).includes(item.dataField));
    assign((column, item) => {
        const display = displayFieldOf(item);
        return Boolean(display) && columnFields(column).includes(display);
    });
    assign((column, item) => mappedTargets(item).some((to) => columnFields(column).includes(to)));
    return result;
};

const isRequiredRule = (rule) => rule && rule.type === 'required';

// Field bắt buộc theo addForm (validationRules / exValidationRules type required).
export const getInlineRequiredFields = (formItems) =>
    flattenFormItems(formItems)
        .filter((item) => !item.viewOnly)
        .filter((item) =>
            [item.validationRules, item.exValidationRules].some((rules) => rules instanceof Array && rules.some(isRequiredRule))
        )
        .map((item) => item.dataField);

const isFilled = (value) => value !== undefined && value !== null && value !== '' && !(value instanceof Array && value.length === 0);

export const getMissingRequiredFields = (row, formItems) => getInlineRequiredFields(formItems).filter((field) => !isFilled(row && row[field]));

// Giá trị mặc định của dòng mới: default từng item addForm + defaultObj.
export const getInlineDefaults = (formItems, defaultObj) => {
    const obj = {};
    flattenFormItems(formItems).forEach((item) => {
        const value = item.default !== undefined ? item.default : item.defaultValue;
        if (value === undefined || value === null) return;
        if (typeof value === 'object' && value.value !== undefined) obj[item.dataField] = value.value;
        else if (typeof value !== 'object') obj[item.dataField] = value;
    });
    if (defaultObj && typeof defaultObj === 'object') Object.assign(obj, defaultObj);
    return obj;
};

// Cột chỉ xem có công thức (vd gross_price) — tạm tính cho dòng nháp khi BE chưa tính.
export const applyInlineFormulas = (row, formItems) => {
    const next = { ...row };
    flattenFormItems(formItems)
        .filter((item) => item.formula instanceof Array && item.formula[0])
        .forEach((item) => {
            const fallback = typeof item.formula[1] === 'number' ? item.formula[1] : 0;
            try {
                next[item.dataField] = calculator.execNum(next, item.formula[0], fallback);
            } catch (e) {
                // Công thức lỗi: giữ giá trị cũ.
            }
        });
    return next;
};

const copyParentFields = (obj, parentData, copyFromParentFields) => {
    if (!parentData || !(copyFromParentFields instanceof Array)) return obj;
    copyFromParentFields.forEach((field) => {
        if (field && typeof field === 'object') {
            const value = parentData[field.field];
            if (value !== undefined && value !== null) obj[field.alias || field.field] = value;
        } else if (field && parentData[field] !== undefined && parentData[field] !== null) obj[field] = parentData[field];
    });
    return obj;
};

// Payload tạo dòng — cùng thứ tự với AntBaseForm.getDataForCreate (popup Thêm):
// dữ liệu dòng (bỏ viewOnly/object/lookups) → lọc postFields → parentFK → copyFromParentFields.
export const buildInlineCreatePayload = ({ row, formItems, postFields, parentFK, parentData, copyFromParentFields }) => {
    const viewOnly = new Set(flattenFormItems(formItems).filter((i) => i.viewOnly).map((i) => i.dataField));
    let obj = {};
    Object.keys(row || {}).forEach((key) => {
        if (viewOnly.has(key)) return;
        if (key === 'id' || key === 'actions' || key === 'lookups' || key === 'warnings' || key === 'checksum' || key.startsWith('__')) return;
        const value = row[key];
        if (value && typeof value === 'object' && !(value instanceof Array)) return;
        obj[key] = value;
    });
    if (postFields instanceof Array) {
        const allowed = new Set(postFields.map((f) => (f && typeof f === 'object' ? f.field : f)));
        Object.keys(obj).forEach((k) => {
            if (!allowed.has(k)) delete obj[k];
        });
    }
    if (parentFK && typeof parentFK === 'object') obj = Object.assign(obj, parentFK);
    obj = copyParentFields(obj, parentData, copyFromParentFields);
    Object.keys(obj).forEach((k) => obj[k] === undefined && delete obj[k]);
    return obj;
};

// Giá trị mặc định lúc mở dòng nháp — cùng AntObjectCreationPopup.show: prepare (BE) + copyFromParentFields.
export const buildInlineDraftBase = ({ prepared, parentData, copyFromParentFields, formItems, defaultObj }) => {
    let base = { ...(prepared && typeof prepared === 'object' ? prepared : {}) };
    delete base.id;
    delete base.code;
    delete base.checksum;
    base = copyParentFields(base, parentData, copyFromParentFields);
    return { ...base, ...getInlineDefaults(formItems, defaultObj) };
};

// Áp mapping của ô chọn danh mục (giống onValueChanged của AntBaseForm).
export const buildLookupPatch = ({ dataField, value, rowData, formDisplayField, displayField, mappings, currentRow }) => {
    const patch = { [dataField]: value };
    if (formDisplayField && displayField && formDisplayField !== dataField)
        patch[formDisplayField] = value !== null && value !== undefined && rowData ? rowData[displayField] : null;
    (mappings instanceof Array ? mappings : []).forEach((m) => {
        if (!m) return;
        if (m.from && m.to) {
            const next = rowData && rowData[m.from] !== undefined ? rowData[m.from] : null;
            if (next === null) {
                if (currentRow && currentRow[m.to] !== null && currentRow[m.to] !== undefined) patch[m.to] = null;
            } else if (!currentRow || currentRow[m.to] !== next) patch[m.to] = next;
        }
        if (m.set && m.value !== undefined) patch[m.set] = m.value;
    });
    return patch;
};

// Suy đoán (khi BE không trả metadata) field KHÔNG cho sửa: khoá/audit, trạng thái, mốc thời gian,
// số tiền tổng/thuế/sau thuế — thường do BE tự tính.
const HEURISTIC_READONLY = /^(id|code|checksum|recalc_checksum|business_code|rowIndex)$|(^|_)status$|_at$|(^|_)(amt|amount|balance)$|^(gross|net|total)_|^(created|updated)_/;

// Lưới KHÔNG cấu hình addForm/editForm: dựng "form ảo" từ cột lưới.
//   meta: metadata BE (create/prepare withInlineFields) — nguồn đúng về field BE tự tính/bắt buộc/mặc định;
//         null/[] → suy đoán theo config/fields của FE.
// Cột *_name hiển thị danh mục thì sửa qua *_code (ô chọn), không sửa chữ tên.
export const synthesizeInlineFormItems = (columns, meta, cnfFields) => {
    const metaMap = new Map((meta instanceof Array ? meta : []).filter((m) => m && m.dataField).map((m) => [m.dataField, m]));
    const cnfMap = new Map((cnfFields instanceof Array ? cnfFields : []).filter((f) => f && f.dataField).map((f) => [f.dataField, f]));
    const editableOf = (field) => {
        if (!field) return null;
        if (metaMap.size > 0) {
            const m = metaMap.get(field);
            return m && m.editable ? m : null;
        }
        const f = cnfMap.get(field);
        if (!f || f.cnfTable || f.viewOnly || f.readOnly || f.formula || HEURISTIC_READONLY.test(field)) return null;
        return { dataField: field, dataType: f.dataType, editable: true, required: false, lookupTable: f.lookup && f.lookup.table ? f.lookup.table : null };
    };
    const isLookupField = (field, m) => Boolean((m && m.lookupTable) || (cnfMap.get(field) && cnfMap.get(field).lookup));
    const toItem = (m) => ({
        dataField: m.dataField,
        dataType: m.dataType,
        validationRules: m.required ? [{ type: 'required' }] : undefined,
        default: m.default,
        _lookupTable: m.lookupTable || undefined
    });
    const items = [];
    const seen = new Set();
    (columns instanceof Array ? columns : []).forEach((column) => {
        if (!column || typeof column !== 'object' || !column.dataField || column.isSystemCol) return;
        if (getConfiguredEditable(column) === false) return;
        const candidates = [column.dataField, ...(column.fields instanceof Array ? column.fields.map((f) => (f && typeof f === 'object' ? f.dataField : f)) : [])];
        for (let i = 0; i < candidates.length; i++) {
            const field = candidates[i];
            if (!field) continue;
            if (/_name$/.test(field)) {
                const codeField = field.replace(/_name$/, '_code');
                const codeMeta = editableOf(codeField);
                if (codeMeta && isLookupField(codeField, codeMeta)) {
                    if (!seen.has(codeField)) items.push(toItem(codeMeta));
                    seen.add(codeField);
                    break;
                }
            }
            const m = editableOf(field);
            if (m) {
                if (!seen.has(field)) items.push(toItem(m));
                seen.add(field);
                break;
            }
        }
    });
    return items;
};

// ---- Lỗi / cảnh báo theo ô và theo dòng (lưới nhập trực tiếp) ----
export const INLINE_TEXTS = {
    required: { vi: 'Bắt buộc nhập', en: 'Required', fr: 'Obligatoire', es: 'Obligatorio', id: 'Wajib diisi', ja: '必須', zh: '必填', ko: '필수 입력', hi: 'आवश्यक', ar: 'مطلوب' },
    missing: { vi: 'Còn thiếu: ', en: 'Missing: ', fr: 'Manquant : ', es: 'Falta: ', id: 'Belum diisi: ', ja: '未入力: ', zh: '缺少：', ko: '누락: ', hi: 'अनुपलब्ध: ', ar: 'ناقص: ' },
    notSaved: { vi: 'Chưa lưu', en: 'Not saved', fr: 'Non enregistré', es: 'No guardado', id: 'Belum disimpan', ja: '未保存', zh: '未保存', ko: '저장되지 않음', hi: 'सहेजा नहीं गया', ar: 'غير محفوظ' },
    rowWarning: { vi: 'Dòng có cảnh báo', en: 'This row has warnings', fr: 'Ligne avec alertes', es: 'Fila con avisos', id: 'Baris ada peringatan', ja: 'この行に警告があります', zh: '该行有警告', ko: '이 행에 경고가 있습니다', hi: 'इस पंक्ति में चेतावनी है', ar: 'صف به تحذيرات' },
    rowError: { vi: 'Không lưu được dòng này', en: 'This row could not be saved', fr: "Impossible d'enregistrer cette ligne", es: 'No se pudo guardar esta fila', id: 'Baris ini tidak dapat disimpan', ja: 'この行を保存できませんでした', zh: '无法保存此行', ko: '이 행을 저장할 수 없습니다', hi: 'यह पंक्ति सहेजी नहीं जा सकी', ar: 'تعذر حفظ هذا الصف' },
    reviewRow: { vi: 'Kiểm tra lại dữ liệu của dòng này', en: 'Please review the data of this row', fr: 'Veuillez vérifier les données de cette ligne', es: 'Revise los datos de esta fila', id: 'Periksa kembali data baris ini', ja: 'この行のデータを確認してください', zh: '请检查此行数据', ko: '이 행의 데이터를 확인하세요', hi: 'कृपया इस पंक्ति का डेटा जाँचें', ar: 'يرجى مراجعة بيانات هذا الصف' },
    newRow: { vi: 'Nhập dòng mới', en: 'New row', fr: 'Nouvelle ligne', es: 'Nueva fila', id: 'Baris baru', ja: '新しい行', zh: '新行', ko: '새 행', hi: 'नई पंक्ति', ar: 'صف جديد' },
    openEdit: { vi: 'Mở form sửa', en: 'Open the edit form', fr: 'Ouvrir l\'édition', es: 'Abrir edición', id: 'Buka formulir edit', ja: '編集フォームを開く', zh: '打开编辑表单', ko: '편집 양식 열기', hi: 'संपादन खोलें', ar: 'فتح نموذج التعديل' },
    clickToEdit: { vi: 'Bấm để mở form sửa', en: 'Click to open the edit form', fr: 'Cliquez pour modifier', es: 'Clic para editar', id: 'Klik untuk edit', ja: 'クリックで編集', zh: '点击打开编辑表单', ko: '클릭하여 편집 양식 열기', hi: 'संपादन हेतु क्लिक करें', ar: 'انقر لفتح نموذج التعديل' }
};

// Nội dung BE trả (chuỗi hoặc {vi, en, ...}) → chuỗi hiển thị; không ra chữ thì ''.
export const inlineMessageText = (message, translate) => {
    if (message === undefined || message === null) return '';
    if (typeof message === 'string') return message.trim();
    if (typeof message === 'object' && typeof translate === 'function') {
        const text = translate(message);
        return typeof text === 'string' ? text.trim() : '';
    }
    return typeof message === 'number' ? String(message) : '';
};

// Field gây lỗi trong kết quả BE lỗi ({ error, field } hoặc { error, data: [{ field }] }).
export const getResultErrorField = (result) => {
    if (!result || !result.error) return null;
    if (result.field) return result.field;
    if (result.data instanceof Array && result.data[0] && result.data[0].field) return result.data[0].field;
    return null;
};

// Lỗi/cảnh báo theo ô của 1 dòng: Map(dataField cột → { level: 'error' | 'warning', messages[] }).
//   missing  : field bắt buộc chưa có giá trị (FE kiểm tra) — lỗi
//   error    : { message, field } BE từ chối lưu — lỗi ở ô của field đó
//   warnings : [{ message, field, type }] BE cảnh báo sau khi lưu — cảnh báo ở ô của field đó
// columnOf(field) → dataField cột trên lưới hiển thị field đó (null nếu không có cột).
export const buildInlineCellIssues = ({ missing = [], error = null, warnings = [], columnOf, translate }) => {
    const issues = new Map();
    const add = (field, level, message) => {
        const column = field && typeof columnOf === 'function' ? columnOf(field) : field;
        if (!column || !message) return;
        const entry = issues.get(column) || { level, messages: [] };
        if (level === 'error') entry.level = 'error';
        if (!entry.messages.includes(message)) entry.messages.push(message);
        issues.set(column, entry);
    };
    const tr = typeof translate === 'function' ? translate : (v) => (typeof v === 'string' ? v : '');
    missing.forEach((field) => add(field, 'error', tr(INLINE_TEXTS.required)));
    if (error && error.field) add(error.field, 'error', inlineMessageText(error.message, translate));
    (warnings || []).forEach((w) => {
        if (w && w.field) add(w.field, w.type === 'Error' ? 'error' : 'warning', inlineMessageText(w.message, translate));
    });
    return issues;
};
